'use client';

import { useCallback, useState } from 'react';
import { useSession } from 'next-auth/react';

import { useModalStore } from '@/store/modal.store';
import { buildDirectBackendUrl, createDirectBackendHeaders } from '@/lib/direct-backend-api';
import {
  applyOptimisticReaction,
  getReactionCount,
  getUserReaction,
} from '../utils/comment-reactions.util';

export function useCommentReactions() {
  const { data: session } = useSession();
  const { setIsOpenAuthDialog } = useModalStore();

  const [reactingComments, setReactingComments] = useState<Set<number>>(new Set());
  const [localReactions, setLocalReactions] = useState<Map<number, Map<string, number>>>(new Map());
  const [localUserReactions, setLocalUserReactions] = useState<Map<number, string | null>>(
    new Map(),
  );
  const [openReactionPopovers, setOpenReactionPopovers] = useState<Set<number>>(new Set());

  const resolveReactionCount = useCallback(
    (commentId: number, reactionType: string, originalCount: number = 0) =>
      getReactionCount(localReactions, commentId, reactionType, originalCount),
    [localReactions],
  );

  const resolveUserReaction = useCallback(
    (commentId: number, originalUserReaction: string | null) =>
      getUserReaction(localUserReactions, commentId, originalUserReaction),
    [localUserReactions],
  );

  const handleReaction = useCallback(
    async (
      commentId: number,
      reactionType: string,
      currentUserReaction: string | null,
      originalReactionsCount: Record<string, number> | null | undefined,
    ) => {
      if (!session) {
        setIsOpenAuthDialog(true);
        return;
      }

      setOpenReactionPopovers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(commentId);
        return newSet;
      });

      const isRemovingReaction = currentUserReaction === reactionType;
      const hadLocalReactions = localReactions.has(commentId);
      const originalReactions = hadLocalReactions ? new Map(localReactions.get(commentId)) : null;

      setLocalReactions((prev) =>
        applyOptimisticReaction(
          prev,
          commentId,
          reactionType,
          currentUserReaction,
          originalReactionsCount,
          isRemovingReaction,
        ),
      );

      setLocalUserReactions((prev) => {
        const newMap = new Map(prev);
        newMap.set(commentId, isRemovingReaction ? null : reactionType);
        return newMap;
      });

      setReactingComments((prev) => new Set(prev).add(commentId));
      try {
        if (!session?.accessToken || !session.user?.id) {
          throw new Error('Требуется авторизация');
        }
        const response = await fetch(buildDirectBackendUrl('/api/comment-reactions'), {
          method: 'POST',
          headers: createDirectBackendHeaders({
            accessToken: session.accessToken,
            hasJsonContentType: true,
          }),
          body: JSON.stringify({
            commentId,
            type: reactionType,
            userId: session.user.id,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to add reaction');
        }
      } catch (error) {
        console.error('Error adding reaction:', error);
        setLocalReactions((prev) => {
          const newMap = new Map(prev);
          if (hadLocalReactions && originalReactions) {
            newMap.set(commentId, originalReactions);
          } else {
            newMap.delete(commentId);
          }
          return newMap;
        });
        setLocalUserReactions((prev) => {
          const newMap = new Map(prev);
          newMap.delete(commentId);
          return newMap;
        });
      } finally {
        setReactingComments((prev) => {
          const newSet = new Set(prev);
          newSet.delete(commentId);
          return newSet;
        });
      }
    },
    [localReactions, session, setIsOpenAuthDialog],
  );

  const setReactionPopoverOpen = useCallback((commentId: number, open: boolean) => {
    setOpenReactionPopovers((prev) => {
      const newSet = new Set(prev);
      if (open) {
        newSet.add(commentId);
      } else {
        newSet.delete(commentId);
      }
      return newSet;
    });
  }, []);

  return {
    reactingComments,
    localReactions,
    openReactionPopovers,
    resolveReactionCount,
    resolveUserReaction,
    handleReaction,
    setReactionPopoverOpen,
  };
}
